import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mail, CheckCircle2, Shield, Check, X } from "lucide-react";
import { BrandLogo } from "@/components/brand-logo";
import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/lib/config";
import { useLanguage } from "@/components/language-provider";
import { formatCpf, isCpfChecksumValid, normalizeCpf, type CpfStatus } from "@/lib/cpf";

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface OAuthPendingData {
  provider: string;
  email: string;
  firstName: string;
  lastName: string;
  providerId: string;
  idToken: string;
}

export default function CompleteRegistration() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { t } = useLanguage();
  
  const [step, setStep] = useState<"info" | "verify">("info");
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [oauthData, setOauthData] = useState<OAuthPendingData | null>(null);
  
  // Form fields
  const [countryCode, setCountryCode] = useState("");
  const [cpf, setCpf] = useState("");
  const [cpfStatus, setCpfStatus] = useState<CpfStatus>("idle");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptElectronicSignature, setAcceptElectronicSignature] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  const { data: countries = [] } = useQuery<Country[]>({
    queryKey: ["/api/auth/countries"],
  });

  const isBrazil = countryCode === "BR";

  useEffect(() => {
    // Load OAuth pending data from sessionStorage
    const pendingData = sessionStorage.getItem("oauth_pending");
    if (!pendingData) {
      toast({
        title: t("ui.session_expired"),
        description: t("ui.please_start_the_registration_process_again"),
        variant: "destructive"
      });
      setLocation("/register");
      return;
    }
    
    try {
      const data = JSON.parse(pendingData) as OAuthPendingData;
      setOauthData(data);
    } catch {
      setLocation("/register");
    }
  }, [setLocation, toast]);

  // Real-time CPF validation: client-side checksum first, then debounced
  // server check for uniqueness. Only runs when the user is on the BR flow
  // and has typed all 11 digits.
  useEffect(() => {
    if (!isBrazil) {
      setCpfStatus("idle");
      return;
    }

    const normalized = normalizeCpf(cpf);

    if (normalized.length === 0) {
      setCpfStatus("idle");
      return;
    }

    if (normalized.length < 11) {
      // Still typing - don't show error yet
      setCpfStatus("idle");
      return;
    }

    // 11 digits typed - run client checksum first (instant)
    if (!isCpfChecksumValid(normalized)) {
      setCpfStatus("invalid_checksum");
      return;
    }

    // Client check passed - debounce server check for uniqueness
    setCpfStatus("checking");
    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/identity/cpf/check`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cpf: normalized }),
          signal: controller.signal,
        });
        const json = await res.json();
        const data = json?.data ?? json;
        const valid = data?.valid ?? false;
        const available = data?.available ?? false;
        const reason = data?.reason as string | null | undefined;

        if (!valid) {
          setCpfStatus("invalid_checksum");
        } else if (!available) {
          setCpfStatus(reason === "already_in_use" ? "already_in_use" : "error");
        } else {
          setCpfStatus("valid");
        }
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        setCpfStatus("error");
      }
    }, 350);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [cpf, isBrazil]);

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCpf(formatCpf(e.target.value));
  };

  const handleSendVerificationCode = async () => {
    if (!oauthData) return;
    
    setSendingCode(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/send-verification-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: oauthData.email }),
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to send code");
      
      setCodeSent(true);
      toast({
        title: t("ui.verification_code_sent"),
        description: `Check your email at ${oauthData.email}`,
      });
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmitInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!acceptTerms || !acceptElectronicSignature) {
      toast({ 
        title: t("ui.consent_required"), 
        description: t("ui.you_must_accept_the_terms_of_service_and_electro"), 
        variant: "destructive" 
      });
      return;
    }

    if (!countryCode) {
      toast({ 
        title: t("ui.country_required"), 
        description: t("ui.please_select_your_country"), 
        variant: "destructive" 
      });
      return;
    }

    if (isBrazil && !cpf) {
      toast({ 
        title: t("ui.cpf_required"), 
        description: t("ui.cpf_is_required_for_brazilian_users"), 
        variant: "destructive" 
      });
      return;
    }

    if (isBrazil && cpfStatus !== "valid") {
      const messages: Record<CpfStatus, string> = {
        idle: "Digite um CPF válido",
        checking: "Aguarde a validação do CPF",
        valid: "",
        invalid_checksum: "CPF inválido. Verifique os números digitados.",
        already_in_use: "Este CPF já está cadastrado em outra conta",
        error: "Erro ao validar o CPF. Tente novamente.",
      };
      toast({
        title: t("ui.cpf_not_validated"),
        description: messages[cpfStatus],
        variant: "destructive",
      });
      return;
    }
    
    // Move to verification step and send code
    setStep("verify");
    handleSendVerificationCode();
  };

  const handleVerifyAndComplete = async () => {
    if (!oauthData || verificationCode.length !== 6) return;
    
    setLoading(true);
    try {
      // Complete OAuth registration with verification
      const res = await fetch(`${API_BASE_URL}/api/auth/oauth/complete-registration`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: oauthData.provider,
          idToken: oauthData.idToken,
          email: oauthData.email,
          firstName: oauthData.firstName,
          lastName: oauthData.lastName,
          countryCode,
          cpf: isBrazil ? cpf : undefined,
          verificationCode,
          acceptTerms,
          acceptElectronicSignature,
        }),
      });
      
      const data = await res.json();
      if (!res.ok) {
        // Surface CPF-specific errors back to the form so the user can fix them.
        const errMsg =
          data.message ||
          data.errors?.[0]?.message ||
          data.errors?.[0] ||
          "Falha no cadastro";

        const lowerMsg = String(errMsg).toLowerCase();
        if (lowerMsg.includes("cpf")) {
          if (lowerMsg.includes("já está cadastrado") || lowerMsg.includes("ja esta cadastrado")) {
            setCpfStatus("already_in_use");
          } else {
            setCpfStatus("invalid_checksum");
          }
          setStep("info");
        }

        throw new Error(errMsg);
      }
      
      const responseData = data.data || data;
      const token = responseData.token || responseData.Token;
      const user = responseData.user || responseData.User;
      
      if (!token || !user) {
        throw new Error("Invalid response from server");
      }
      
      // Clear pending data
      sessionStorage.removeItem("oauth_pending");
      sessionStorage.removeItem("oauth_register");
      
      // Save auth data
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      
      toast({
        title: t("ui.account_created_1"),
        description: t("ui.welcome_to_fillsign"),
      });
      
      setLocation("/dashboard");
      
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!oauthData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <BrandLogo />
          </div>
          <CardTitle>{t("register.completeTitle") || "Complete Your Registration"}</CardTitle>
          <CardDescription>
            {step === "info" 
              ? t("register.completeDesc") || "Just a few more details to set up your account"
              : t("register.verifyDesc") || "Enter the verification code sent to your email"
            }
          </CardDescription>
          
          {/* User info from OAuth */}
          <div className="mt-4 p-3 bg-muted rounded-lg text-sm">
            <div className="flex items-center gap-2 justify-center">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>{t("ui.signed_in_as")} <strong>{oauthData.email}</strong></span>
            </div>
            {oauthData.firstName && (
              <p className="text-muted-foreground mt-1">
                {oauthData.firstName} {oauthData.lastName}
              </p>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {step === "info" ? (
            <form onSubmit={handleSubmitInfo} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="country">{t("register.country") || "Country"}</Label>
                <Select value={countryCode} onValueChange={setCountryCode}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("register.selectCountry") || "Select your country"} />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t("register.countryHelp") || "Required for identity verification"}
                </p>
              </div>

              {isBrazil && (
                <div className="space-y-2">
                  <Label htmlFor="cpf">CPF</Label>
                  <div className="relative">
                    <Input
                      id="cpf"
                      type="text"
                      placeholder="000.000.000-00"
                      value={cpf}
                      onChange={handleCpfChange}
                      maxLength={14}
                      required
                      className={
                        cpfStatus === "valid"
                          ? "pr-10 border-green-500 focus-visible:ring-green-500"
                          : cpfStatus === "invalid_checksum" || cpfStatus === "already_in_use" || cpfStatus === "error"
                          ? "pr-10 border-red-500 focus-visible:ring-red-500"
                          : "pr-10"
                      }
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {cpfStatus === "checking" && (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                      {cpfStatus === "valid" && (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                      {(cpfStatus === "invalid_checksum" ||
                        cpfStatus === "already_in_use" ||
                        cpfStatus === "error") && (
                        <X className="h-4 w-4 text-red-600" />
                      )}
                    </div>
                  </div>
                  {cpfStatus === "idle" && (
                    <p className="text-xs text-muted-foreground">
                      {t("register.cpfHelp") || "Seu CPF é usado para evitar contas duplicadas"}
                    </p>
                  )}
                  {cpfStatus === "checking" && (
                    <p className="text-xs text-muted-foreground">Validando CPF...</p>
                  )}
                  {cpfStatus === "valid" && (
                    <p className="text-xs text-green-600">CPF válido</p>
                  )}
                  {cpfStatus === "invalid_checksum" && (
                    <p className="text-xs text-red-600">CPF inválido. Verifique os números digitados.</p>
                  )}
                  {cpfStatus === "already_in_use" && (
                    <p className="text-xs text-red-600">Este CPF já está cadastrado em outra conta.</p>
                  )}
                  {cpfStatus === "error" && (
                    <p className="text-xs text-red-600">Erro ao validar o CPF. Tente novamente.</p>
                  )}
                </div>
              )}

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="acceptTerms"
                    checked={acceptTerms}
                    onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-muted-foreground cursor-pointer leading-tight">
                    {t("register.acceptTerms") || "I agree to the Terms of Service and Privacy Policy"}
                  </label>
                </div>
                
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="acceptElectronicSignature"
                    checked={acceptElectronicSignature}
                    onCheckedChange={(checked) => setAcceptElectronicSignature(checked === true)}
                  />
                  <label htmlFor="acceptElectronicSignature" className="text-sm text-muted-foreground cursor-pointer leading-tight">
                    {t("register.acceptElectronic") || "I consent to conduct business electronically"}
                  </label>
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={
                  !acceptTerms ||
                  !acceptElectronicSignature ||
                  !countryCode ||
                  (isBrazil && cpfStatus !== "valid")
                }
              >
                {t("common.next") || "Next"}: {t("register.verifyEmail") || "Verify Email"}
              </Button>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  {t("register.codeSentTo") || "We sent a verification code to"}<br />
                  <strong>{oauthData.email}</strong>
                </p>
              </div>
              
              <div className="flex flex-col items-center gap-4">
                <InputOTP
                  maxLength={6}
                  pattern={REGEXP_ONLY_DIGITS}
                  value={verificationCode}
                  onChange={(value) => {
                    // Defense in depth: strip any non-digit that slipped through
                    // (e.g. when pasting an email-formatted code with stray whitespace).
                    setVerificationCode(value.replace(/\D/g, "").slice(0, 6));
                  }}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleSendVerificationCode}
                  disabled={sendingCode}
                >
                  {sendingCode ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("ui.sending")}
                    </>
                  ) : (
                    t("register.resendCode") || "Resend code"
                  )}
                </Button>
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setStep("info")}
                  className="flex-1"
                >
                  {t("common.back") || "Back"}
                </Button>
                <Button 
                  onClick={handleVerifyAndComplete}
                  disabled={loading || verificationCode.length !== 6}
                  className="flex-1"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("register.createAccount") || "Create Account"}
                </Button>
              </div>
              
              <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center">
                <Shield className="h-3 w-3" />
                {t("register.secureVerification") || "Secure email verification"}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
